import React, { useState, useMemo, useEffect } from 'react'
import { IconButton } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import PlayArrowIcon from "@mui/icons-material/PlayArrow"

import { useSnackbar } from '../snackbar/snackbar'
import { DashboardBody, DashboardHeader } from '../dashboard-container'
import RequestTable from '../request-table/generic'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import BuildIcon from '@mui/icons-material/Build'

import useFetch from '../../hooks/useFetch'
import { listBlueprintVersionById, listBlueprintVersion, deleteVersionById } from '../../api-client/data'
import Button from '../nura/button/button'
import useRefMenu from '../../hooks/useRefMenu'

import styles from "./styles.module.css"
import CreateVersion from '../dialogs/create-version'
import DeleteVersionDialog from '../dialogs/delete-version'

import { createNewBlueprintVersion } from '../../api-client/data'
import { useDispatch, useSelector, Actions } from '../../stores/dashboard.store'

import MoreOptions from '../forms/more-options'
import { formatDateTimeFancy } from '../../util/time'
import ExampleVersion from '../dialogs/example-version'

const BlueprintVersionActions = ({ data, setExampleShow }) => {    
    const menu = useRefMenu()
    const dispatch = useDispatch()

    const runBlueprintVersion = async (e) => {
        e.stopPropagation()

        const ret = await listBlueprintVersionById(data?.id)
        dispatch(Actions.SetRunProject, { ...ret, specificVersionId: data?.id })
    }

    const showExample = async (e) => {
        e.stopPropagation()

        setExampleShow(data)
    }

    return (
        <div ref={menu.ref}>
            <IconButton onClick={() => menu.setShow(true)}>
                <MoreVertIcon style={{ color: `var(--text-primary)` }} />
            </IconButton>
            <MoreOptions
                buttonRef={menu.ref}
                open={menu.show}
                setOpen={menu.setShow}
                options={[
                    { name: "Run", onClick: runBlueprintVersion, icon: () => <PlayArrowIcon /> },
                    { name: "Example", onClick: showExample, icon: () => <BuildIcon style={{ fontSize: '1rem', marginRight: 2, marginLeft: 5 }}/> },
                ]}
            />
        </div>
    )
}



const BlueprintVersion = () => {
    const { activeProject } = useSelector()
    const { enqueueSnackbar } = useSnackbar()

    const versions = useFetch(() => listBlueprintVersion(activeProject?.id), [])

    const [showDialog, setShowDialog] = useState(false)
    const [checkedVersions, setCheckedVersions] = useState([])
    const [deleteVersionShow, setDeleteVersionShow] = useState(false)
    const [exampleShow, setExampleShow] = useState()

    const createNewVersion = async () => {
        await createNewBlueprintVersion({
            blueprintId: activeProject?.id,
            isSmart: !!activeProject?.nodes.find(x => x?.data?.isSmart)
        })
        versions.refresh()

        setShowDialog(false)
    }

    const copyContentToClipboard = (e, id) => {
        e.stopPropagation()

        const tempInput = document.createElement("input");
        tempInput.value = id
        document.body.appendChild(tempInput)
        tempInput.select()
        document.execCommand("copy")
        document.body.removeChild(tempInput)

        enqueueSnackbar("Version ID copied to clipboard", { variant: "info" })
    }

    const updatedData = useMemo(() => versions.data?.map(x => ({
        _id: x?.id,
        id: () => (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', alignItems: 'center' }}>
                <div>{x?.id}</div>
                <div><ContentCopyIcon onClick={(e) => copyContentToClipboard(e, x?.id)} style={{ fontSize: '1rem'}}/></div>
            </div>
        ),
        createdAt: formatDateTimeFancy(new Date(x?.createdAt)),
        run: () => (
            <BlueprintVersionActions setExampleShow={setExampleShow} data={x} />
        ), 
    })), [versions?.data])

    const deleteSelectedVersions = async () => {
        try {
            await Promise.all(checkedVersions.map(async (id) => await deleteVersionById(id)))
            versions.refresh()
            setDeleteVersionShow(false)
        } catch {
            enqueueSnackbar("Something went wrong deleting 1 or more versions", { variant: "error" })
        }
    }

    return (
        <div className={styles.dashboardContent}>
            <ExampleVersion show={!!exampleShow} onClose={() => setExampleShow(undefined)} details={exampleShow}/>
            <CreateVersion
                show={showDialog}
                onClose={() => setShowDialog(false)}
                onSubmit={createNewVersion}
            />
            <DeleteVersionDialog
                show={deleteVersionShow}
                setShow={setDeleteVersionShow}
                handleDelete={deleteSelectedVersions}
            />
            <DashboardHeader title="Versions">
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                    <Button size="normal" variant="fancy" onClick={() => setShowDialog(true)}>New Version</Button>
                    <Button size="normal" variant="error" disabled={checkedVersions.length === 0} onClick={() => setDeleteVersionShow(true)}>Delete</Button>
                    <IconButton style={{ color: 'var(--text-primary)', width: 40, height: 40 }} onClick={versions.refresh}>
                        <RefreshIcon style={{ fontSize: 20 }} />
                    </IconButton>
                </div>
            </DashboardHeader>
            <DashboardBody variant="nopad" loading={versions?.loading}>
                <RequestTable
                    onClick={() => { }}
                    data={updatedData}
                    hiddenHeaderKeys={["run"]}
                    setChecked={setCheckedVersions}
                />
            </DashboardBody>

        </div>
    )
}

export default BlueprintVersion